import { glossary, phrases } from '../data/translations'
import { beaconsfieldPhrases } from '../data/translationsBeaconsfield'

/** Le complément de Beaconsfield est fusionné par-dessus le dictionnaire commun :
    une entrée locale gagne sur la formulation générique. */
const phraseMap = new Map<string, string>(
  Object.entries({ ...phrases, ...beaconsfieldPhrases }).map(([french, english]) => [
    normalize(french),
    english,
  ]),
)

const MONTHS: Record<string, string> = {
  janvier: 'January',
  février: 'February',
  mars: 'March',
  avril: 'April',
  mai: 'May',
  juin: 'June',
  juillet: 'July',
  août: 'August',
  septembre: 'September',
  octobre: 'October',
  novembre: 'November',
  décembre: 'December',
}

const MONTH = `(${Object.keys(MONTHS).join('|')})`

function normalize(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase()
}

/** 9 h 42 → 9:42 a.m. ; 16 h 20 → 4:20 p.m. */
function formatTime(hours: string, minutes: string): string {
  const h = Number(hours)
  const suffix = h >= 12 ? 'p.m.' : 'a.m.'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${minutes} ${suffix}`
}

/** Traduit un fragment dynamique s'il figure au dictionnaire, sinon le laisse intact. */
function exactOrSame(value: string): string {
  return translateText(value)
}

type Pattern = [RegExp, (match: RegExpMatchArray) => string]

/** Libellés composés d'une partie fixe et d'une donnée (date, heure, nom de fichier…). */
const patterns: Pattern[] = [
  [new RegExp(`^(\\d{1,2}) ${MONTH}, (\\d{1,2}) h (\\d{2})$`, 'i'), (m) =>
    `${MONTHS[m[2].toLowerCase()]} ${m[1]}, ${formatTime(m[3], m[4])}`],
  [new RegExp(`^(\\d{1,2}) ${MONTH}$`, 'i'), (m) => `${MONTHS[m[2].toLowerCase()]} ${m[1]}`],
  [new RegExp(`^Envoyée le (\\d{1,2}) ${MONTH}$`, 'i'), (m) => `Sent on ${MONTHS[m[2].toLowerCase()]} ${m[1]}`],
  [/^Aujourd’hui, (\d{1,2}) h (\d{2})$/, (m) => `Today, ${formatTime(m[1], m[2])}`],
  [/^il y a (.+)$/i, (m) => `${m[1]} ago`],
  [/^Télécharger (.+)$/, (m) => `Download ${m[1]}`],
  [/^Statut : (.+)$/, (m) => `Status: ${exactOrSame(m[1])}`],
  [/^Secteur (\S+)$/, (m) => `Sector ${m[1]}`],
  [/^Camion (\S+)$/, (m) => `Truck ${m[1]}`],
  [/^Bac non ramassé — (.+)$/, (m) => `Bin not picked up — ${exactOrSame(m[1])}`],
  [
    /^Le bac de (.+) n’a pas été ramassé lors de la collecte du jour \((.+), (.+)\)\. Le bac est resté au bord de la rue\.$/,
    (m) =>
      `The ${exactOrSame(m[1]).toLowerCase()} bin was not picked up during today’s collection (${exactOrSame(
        m[2],
      )}, ${exactOrSame(m[3])}). The bin was left at the curb.`,
  ],
]

/** Applique la casse du mot source au mot traduit. */
function matchCase(source: string, target: string): string {
  if (source === source.toUpperCase() && source.length > 1) return target.toUpperCase()
  if (source[0] === source[0]?.toUpperCase()) return target.charAt(0).toUpperCase() + target.slice(1)
  return target
}

/**
 * Traduit un libellé du français vers l'anglais :
 * 1. correspondance exacte au dictionnaire ;
 * 2. libellés composés (dates, heures, fichiers…) ;
 * 3. repli mot à mot, appliqué SEULEMENT si chaque mot est connu —
 *    sinon le texte reste en français plutôt que de produire du franglais.
 */
export function translateText(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return value

  const exact = phraseMap.get(normalize(trimmed))
  if (exact) return value.replace(trimmed, exact)

  for (const [pattern, build] of patterns) {
    const match = trimmed.match(pattern)
    if (match) return value.replace(trimmed, build(match))
  }

  const words = trimmed.match(/[A-Za-zÀ-ÿ’']+/g) ?? []
  const allKnown = words.length > 0 && words.every((word) => glossary[word.toLowerCase()] !== undefined)
  if (!allKnown) return value

  const translated = trimmed.replace(/[A-Za-zÀ-ÿ’']+/g, (word) => matchCase(word, glossary[word.toLowerCase()]))
  return translated === trimmed ? value : value.replace(trimmed, translated)
}
